import type {
  CheckStatusResult,
  GenerationJob,
  ImageRequest,
  Model,
  ProviderAdapter,
  VideoRequest,
} from '@byokapp/types'
import { ProviderError } from '@byokapp/types'

const PROXY_PATH = '/api/cloudflare-generate'
const PROVIDER = 'cloudflare-ai'

// Browser resolves relative URLs against window.location.origin automatically.
// Node.js (Vitest) requires an absolute URL for fetch to work.
function proxyUrl(): string {
  return typeof window !== 'undefined' ? PROXY_PATH : `http://localhost${PROXY_PATH}`
}
const RESULT_TTL_MS = 10 * 60 * 1000

const MODELS: Model[] = [
  {
    id: '@cf/black-forest-labs/flux-2-dev',
    name: 'FLUX.2 Dev',
    type: 'image',
    provider: PROVIDER,
  },
  {
    id: '@cf/black-forest-labs/flux-2-klein-4b',
    name: 'FLUX.2 Klein 4B',
    type: 'image',
    provider: PROVIDER,
  },
]

const ASPECT_RATIO_DIMS: Record<string, { width: number; height: number }> = {
  '1:1': { width: 1024, height: 1024 },
  '16:9': { width: 1024, height: 576 },
  '9:16': { width: 576, height: 1024 },
  '4:3': { width: 1024, height: 768 },
  '3:4': { width: 768, height: 1024 },
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseApiKey(apiKey: string): { accountId: string; gatewaySlug: string; apiToken: string } {
  const first = apiKey.indexOf(':')
  const second = apiKey.indexOf(':', first + 1)
  if (first === -1 || second === -1)
    throw new ProviderError({
      code: 'AUTH_ERROR',
      message: 'Cloudflare key must be in the format ACCOUNT_ID:GATEWAY_SLUG:API_TOKEN',
      provider: PROVIDER,
      retryable: false,
    })
  const accountId = apiKey.slice(0, first)
  const gatewaySlug = apiKey.slice(first + 1, second)
  const apiToken = apiKey.slice(second + 1)
  if (!/^[a-zA-Z0-9_-]+$/.test(accountId) || !/^[a-zA-Z0-9_-]+$/.test(gatewaySlug))
    throw new ProviderError({
      code: 'AUTH_ERROR',
      message: 'Account ID and Gateway Slug must be alphanumeric (dashes/underscores allowed)',
      provider: PROVIDER,
      retryable: false,
    })
  return { accountId, gatewaySlug, apiToken }
}

function resolveDimensions(aspectRatio?: string): { width: number; height: number } {
  return ASPECT_RATIO_DIMS[aspectRatio ?? '1:1'] ?? { width: 1024, height: 1024 }
}

function extractImage(body: unknown): string {
  const b = body as { result?: { image?: unknown }; success?: boolean }
  const image = b?.result?.image
  if (typeof image !== 'string' || image.length === 0)
    throw new ProviderError({
      code: 'UNKNOWN',
      message: 'Cloudflare returned no image in the response.',
      provider: PROVIDER,
      retryable: false,
    })
  return `data:image/jpeg;base64,${image}`
}

async function mapHttpError(res: Response): Promise<never> {
  let message = `HTTP ${res.status}`
  try {
    const body = (await res.json()) as { errors?: Array<{ code?: number; message?: string }> }
    const first = body.errors?.[0]
    if (first?.message) message = first.message
  } catch {
    // body may not be JSON — fall through to status-based message
  }
  if (res.status === 401 || res.status === 403)
    throw new ProviderError({ code: 'AUTH_ERROR', message, provider: PROVIDER, retryable: false })
  if (res.status === 429)
    throw new ProviderError({ code: 'RATE_LIMITED', message, provider: PROVIDER, retryable: true })
  if (res.status >= 500)
    throw new ProviderError({ code: 'PROVIDER_DOWN', message, provider: PROVIDER, retryable: true })
  throw new ProviderError({ code: 'UNKNOWN', message, provider: PROVIDER, retryable: false })
}

// ─── Adapter ──────────────────────────────────────────────────────────────────

export class CloudflareWorkersAiAdapter implements ProviderAdapter {
  readonly provider = PROVIDER

  readonly #results = new Map<string, { value: CheckStatusResult; storedAt: number }>()

  #pruneStale() {
    const now = Date.now()
    for (const [id, { storedAt }] of this.#results) {
      if (now - storedAt > RESULT_TTL_MS) this.#results.delete(id)
    }
  }

  async generateImage(request: ImageRequest, apiKey: string): Promise<GenerationJob> {
    this.#pruneStale()
    const { accountId, gatewaySlug, apiToken } = parseApiKey(apiKey)
    const dims = resolveDimensions(request.aspectRatio)

    let res: Response
    try {
      res = await fetch(proxyUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: request.prompt,
          model: request.model,
          width: dims.width,
          height: dims.height,
          accountId,
          gatewaySlug,
          apiToken,
        }),
      })
    } catch {
      throw new ProviderError({
        code: 'UNKNOWN',
        message: 'Network error — check connectivity',
        provider: PROVIDER,
        retryable: true,
      })
    }

    if (!res.ok) await mapHttpError(res)

    let responseBody: unknown
    try {
      responseBody = await res.json()
    } catch {
      throw new ProviderError({
        code: 'UNKNOWN',
        message: 'Cloudflare returned an unexpected response format.',
        provider: PROVIDER,
        retryable: false,
      })
    }
    const dataUrl = extractImage(responseBody)

    const jobId = crypto.randomUUID()
    this.#results.set(jobId, {
      value: { status: 'succeeded', outputUrls: [dataUrl] },
      storedAt: Date.now(),
    })
    return { jobId, status: 'succeeded' }
  }

  async generateVideo(_request: VideoRequest, _apiKey: string): Promise<GenerationJob> {
    throw new ProviderError({
      code: 'VALIDATION_FAILED',
      message: 'Cloudflare Workers AI does not support video generation.',
      provider: PROVIDER,
      retryable: false,
    })
  }

  async checkStatus(jobId: string, _apiKey: string): Promise<CheckStatusResult> {
    this.#pruneStale()
    const entry = this.#results.get(jobId)
    if (entry !== undefined) {
      this.#results.delete(jobId)
      return entry.value
    }
    throw new ProviderError({
      code: 'NOT_FOUND',
      message: `No job found with id: ${jobId}`,
      provider: PROVIDER,
      retryable: false,
    })
  }

  async listModels(_apiKey: string): Promise<Model[]> {
    return [...MODELS]
  }
}
